import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiGet } from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import './EstadisticasPage.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtEur(n) {
  if (!n || isNaN(n)) return '0,00 €';
  return Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function fmtNum(n) { return Number(n || 0).toLocaleString('es-ES'); }
function fmtPct(n) { return (Number(n || 0).toFixed(1)) + '%'; }

const STATUS_LABEL = {
  nuevo:       'Nuevo',
  contactado:  'Contactado',
  no_responde: 'No responde',
  seguimiento: 'Seguimiento',
  ganado:      'Ganado',
  perdido:     'Perdido',
};
const STATUS_COLOR = {
  nuevo:       '#3b82f6',
  contactado:  '#f59e0b',
  no_responde: '#94a3b8',
  seguimiento: '#8b5cf6',
  ganado:      '#22c55e',
  perdido:     '#ef4444',
};
const FUNNEL_ORDER = ['nuevo','contactado','no_responde','seguimiento','ganado','perdido'];

const SOURCE_COLOR = {
  facebook: '#1877f2',
  manual:   '#64748b',
  whatsapp: '#25d366',
  web:      '#e84300',
};
function sourceColor(s) { return SOURCE_COLOR[s?.toLowerCase()] || '#94a3b8'; }
function sourceLabel(s) {
  const m = { facebook:'Facebook Ads', manual:'Manual', whatsapp:'WhatsApp', web:'Web' };
  return m[s?.toLowerCase()] || s || 'Otro';
}

// Fecha hoy en YYYY-MM-DD
function today() { return new Date().toISOString().slice(0,10); }
function monthStart() {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0,10);
}
function weekStart() {
  const d = new Date();
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  return d.toISOString().slice(0,10);
}
function yearStart() { return `${new Date().getFullYear()}-01-01`; }

// ── Componentes UI ────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color = 'var(--primary)' }) {
  return (
    <div className="est-kpi">
      <div className="est-kpi-label">{label}</div>
      <div className="est-kpi-value" style={{ color }}>{value}</div>
      {sub && <div className="est-kpi-sub">{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }) {
  return <h2 className="est-section-title">{children}</h2>;
}

// ── Embudo ────────────────────────────────────────────────────────────────────

function Funnel({ byStatus }) {
  const statusMap = {};
  (byStatus || []).forEach(s => { statusMap[s.status] = Number(s.count); });
  const total = FUNNEL_ORDER.reduce((s,k) => s + (statusMap[k]||0), 0) || 1;

  return (
    <div className="est-funnel">
      {FUNNEL_ORDER.map((st, i) => {
        const count = statusMap[st] || 0;
        const pct   = Math.round((count / total) * 100);
        const width = Math.max(20, pct);
        return (
          <div key={st} className="est-funnel-row">
            <div className="est-funnel-label">{STATUS_LABEL[st]}</div>
            <div className="est-funnel-bar-wrap">
              <div
                className="est-funnel-bar"
                style={{ width: `${width}%`, background: STATUS_COLOR[st] }}
              />
            </div>
            <div className="est-funnel-count">
              <strong>{fmtNum(count)}</strong>
              <span>{fmtPct(pct)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Gráfica entrada leads ─────────────────────────────────────────────────────

function LeadChart({ byDate }) {
  // Agrupar por fecha y pivotar sources
  const dateMap = {};
  const sourcesSet = new Set();
  (byDate || []).forEach(({ date, source, count }) => {
    const d = String(date).slice(0,10);
    if (!dateMap[d]) dateMap[d] = { date: d };
    dateMap[d][source] = (dateMap[d][source] || 0) + Number(count);
    sourcesSet.add(source);
  });
  const data    = Object.values(dateMap).sort((a,b) => a.date.localeCompare(b.date));
  const sources = [...sourcesSet];

  if (!data.length) return <div className="est-empty">Sin datos para el período</div>;

  const fmtDate = (d) => {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('es-ES', { day:'2-digit', month:'2-digit' });
  };

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11 }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
        <Tooltip
          labelFormatter={fmtDate}
          formatter={(v, name) => [v, sourceLabel(name)]}
          contentStyle={{ fontSize: 12, borderRadius: 8 }}
        />
        <Legend formatter={sourceLabel} wrapperStyle={{ fontSize: 12 }} />
        {sources.map(src => (
          <Bar key={src} dataKey={src} stackId="a" fill={sourceColor(src)} name={src} radius={sources.indexOf(src) === sources.length-1 ? [3,3,0,0] : [0,0,0,0]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Tabla comerciales ─────────────────────────────────────────────────────────

function ComercialTable({ byUser }) {
  if (!byUser?.length) return (
    <div className="est-table-wrap">
      <table className="est-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Asignados</th>
            <th>Ganados</th>
            <th>Perdidos</th>
            <th>Activos</th>
            <th>Conversión</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={7} style={{ textAlign:'center', padding:'2rem', color:'var(--muted)', fontSize:'.85rem' }}>
              Sin datos para el período seleccionado
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="est-table-wrap">
      <table className="est-table">
        <thead>
          <tr>
            <th>Comercial</th>
            <th>Asignados</th>
            <th>Ganados</th>
            <th>Perdidos</th>
            <th>Activos</th>
            <th>Conversión</th>
            <th style={{ width: 160 }}></th>
          </tr>
        </thead>
        <tbody>
          {byUser.map((u, i) => {
            const conv = u.total > 0 ? Math.round((u.ganado / u.total) * 1000) / 10 : 0;
            return (
              <tr key={i}>
                <td className="est-td-name">{u.name || 'Sin asignar'}</td>
                <td><strong>{fmtNum(u.total)}</strong></td>
                <td><span className="est-badge est-badge--green">{fmtNum(u.ganado)}</span></td>
                <td><span className="est-badge est-badge--red">{fmtNum(u.perdido)}</span></td>
                <td><span className="est-badge est-badge--blue">{fmtNum(u.activos)}</span></td>
                <td>
                  <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
                    <div className="est-conv-bar-wrap">
                      <div className="est-conv-bar" style={{ width: `${Math.min(conv,100)}%` }} />
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600, minWidth: 36 }}>{fmtPct(conv)}</span>
                  </div>
                </td>
                <td>
                  <div style={{ display:'flex', gap: 6 }}>
                    <MiniBar label="G" value={u.ganado} total={u.total} color="#22c55e" />
                    <MiniBar label="P" value={u.perdido} total={u.total} color="#ef4444" />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MiniBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap: 2 }}>
      <div style={{ width: 20, height: 40, background: '#f1f5f9', borderRadius: 3, display:'flex', alignItems:'flex-end', overflow:'hidden' }}>
        <div style={{ width: '100%', height: `${pct}%`, background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 700 }}>{label}</span>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const PRESETS = [
  { key: 'today',  label: 'Hoy' },
  { key: 'week',   label: 'Esta semana' },
  { key: 'month',  label: 'Este mes' },
  { key: 'year',   label: 'Este año' },
  { key: 'custom', label: 'Personalizado' },
];

function presetDates(key) {
  const t = today();
  switch (key) {
    case 'today': return { from: t, to: t };
    case 'week':  return { from: weekStart(), to: t };
    case 'month': return { from: monthStart(), to: t };
    case 'year':  return { from: yearStart(), to: t };
    default:      return null;
  }
}

export default function EstadisticasPage() {
  const { auth } = useContext(AuthContext);
  const token      = auth.token;
  const role       = auth.user?.role;
  const isAdmin    = ['admin','superadmin'].includes(role);
  const isEmpresa  = role === 'empresa-cliente';
  const modules    = auth.modules || [];
  const hasCalc    = modules.includes('calculadora') || role === 'superadmin';

  const [preset,     setPreset]     = useState('month');
  const [customFrom, setCustomFrom] = useState(monthStart());
  const [customTo,   setCustomTo]   = useState(today());
  const [userId,     setUserId]     = useState('');
  const [subTenantId,setSubTenantId]= useState('');

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const dates = preset !== 'custom' ? presetDates(preset) : { from: customFrom, to: customTo };
      if (!dates) return;
      const p = new URLSearchParams({ from: dates.from, to: dates.to });
      if (userId)      p.set('user_id',      userId);
      if (subTenantId) p.set('sub_tenant_id', subTenantId);
      const result = await apiGet(`/stats?${p}`, token);
      setData(result);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token, preset, customFrom, customTo, userId, subTenantId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const s        = data?.summary || {};
  const byStatus = data?.byStatus || [];
  const bySource = data?.bySource || [];
  const byDate   = data?.byDate   || [];
  const byUser       = data?.byUser       || [];
  const bySubTenant  = data?.bySubTenant  || [];
  const quotes   = data?.quotes   || {};
  const invoices = data?.invoices || {};
  const users    = data?.users    || [];
  const subT     = data?.subTenants || [];

  return (
    <div className="est-page">

      {/* ── Filtros ── */}
      <div className="est-filters">
        <div className="est-presets">
          {PRESETS.map(p => (
            <button
              key={p.key}
              className={`est-preset${preset === p.key ? ' est-preset--on' : ''}`}
              onClick={() => setPreset(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {preset === 'custom' && (
          <div className="est-custom-dates">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="est-date-input" />
            <span style={{ color:'#94a3b8', fontSize: 12 }}>—</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="est-date-input" />
          </div>
        )}

        {isAdmin && users.length > 0 && (
          <select className="est-filter-sel" value={userId} onChange={e => setUserId(e.target.value)}>
            <option value="">Todos los comerciales</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}

        {isAdmin && subT.length > 0 && (
          <select className="est-filter-sel" value={subTenantId} onChange={e => setSubTenantId(e.target.value)}>
            <option value="">Todas las empresas</option>
            {subT.map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
          </select>
        )}
      </div>

      {loading && <div className="est-loading">Cargando estadísticas…</div>}
      {error   && <div className="est-error">Error: {error}</div>}

      {!loading && !error && data && (
        <>
          {/* ── S1: Resumen ── */}
          <SectionTitle>Resumen general</SectionTitle>
          <div className="est-kpi-grid">
            <KpiCard label="Total leads" value={fmtNum(s.total)} sub={`Período seleccionado`} />
            <KpiCard label="Leads ganados" value={fmtNum(s.ganado)} color="#22c55e" sub="Convertidos a cliente" />
            <KpiCard label="Tasa de conversión" value={fmtPct(s.conversion_rate)} color={s.conversion_rate >= 20 ? '#22c55e' : '#f59e0b'} sub="Ganados / total" />
            <KpiCard label="Leads perdidos" value={fmtNum(s.perdido)} color="#ef4444" />
            <KpiCard label="En seguimiento" value={fmtNum(s.activos)} color="#3b82f6" sub="Activos (no cerrados)" />
          </div>

          {/* ── S2: Embudo ── */}
          <SectionTitle>Embudo de ventas</SectionTitle>
          <div className="est-card">
            <Funnel byStatus={byStatus} />
          </div>

          {/* ── S3: Gráfica leads ── */}
          <SectionTitle>Entrada de leads</SectionTitle>
          <div className="est-card">
            {bySource.length > 0 && (
              <div className="est-source-pills">
                {bySource.map(s => (
                  <span key={s.source} className="est-source-pill" style={{ background: sourceColor(s.source) + '22', color: sourceColor(s.source), border: `1px solid ${sourceColor(s.source)}44` }}>
                    <span className="est-source-dot" style={{ background: sourceColor(s.source) }} />
                    {sourceLabel(s.source)}: <strong>{fmtNum(s.count)}</strong>
                  </span>
                ))}
              </div>
            )}
            <LeadChart byDate={byDate} />
          </div>

          {/* ── S4: Comerciales (solo admin) ── */}
          {isAdmin && (
            <>
              <SectionTitle>Rendimiento por comercial</SectionTitle>
              <div className="est-card est-card--no-pad">
                <ComercialTable byUser={byUser} />
              </div>
            </>
          )}

          {/* ── S5: Empresas cliente (solo admin) ── */}
          {isAdmin && (
            <>
              <SectionTitle>Rendimiento por empresa-cliente</SectionTitle>
              <div className="est-card est-card--no-pad">
                <ComercialTable byUser={bySubTenant.filter(s => s.name !== 'Sin empresa')} />
              </div>
            </>
          )}

          {/* ── Info empresa-cliente: sus propios datos ── */}
          {isEmpresa && (
            <div style={{ background:'var(--brand-light)', border:'1px solid var(--primary)22', borderRadius:10, padding:'0.85rem 1.1rem', marginBottom:'0.75rem', fontSize:'.85rem', color:'var(--text)' }}>
              Estás viendo las estadísticas de tu empresa. Los datos se filtran automáticamente a tu actividad.
            </div>
          )}

          {/* ── S6: Presupuestos (si tiene módulo calculadora) ── */}
          {hasCalc && (
            <>
              <SectionTitle>Calculadora y presupuestos</SectionTitle>
              <div className="est-kpi-grid">
                <KpiCard label="Presupuestos generados" value={fmtNum(quotes.total)} />
                <KpiCard label="Importe total presupuestado" value={fmtEur(quotes.total_amount)} color="#8b5cf6" />
                <KpiCard label="Presupuestos aceptados" value={fmtNum(quotes.accepted)} color="#22c55e" />
                <KpiCard label="Presupuestos rechazados" value={fmtNum(quotes.rejected)} color="#ef4444" />
                <KpiCard label="Leads con presupuesto" value={fmtNum(quotes.leads_with_quote)} sub="Con al menos un presupuesto" />
              </div>
            </>
          )}

          {/* ── S6: Facturación ── */}
          <SectionTitle>Facturación</SectionTitle>
          <div className="est-kpi-grid">
            <KpiCard
              label="Facturación total"
              value={fmtEur(invoices.total_amount)}
              color={invoices.total > 0 ? '#22c55e' : '#94a3b8'}
              sub={invoices.total > 0 ? `${fmtNum(invoices.total)} facturas` : 'Sin facturas en el período'}
            />
            <KpiCard label="Ticket medio" value={fmtEur(invoices.avg_ticket)} color="#8b5cf6" />
            <KpiCard label="Nº de facturas" value={fmtNum(invoices.total)} />
          </div>
        </>
      )}
    </div>
  );
}
